import React, {useCallback, useMemo, useState} from "react";
import {observer} from "mobx-react";
import ReactAwesomePlayer from "react-awesome-player";
import styled from "styled-components";
import {useIsMobile, useStore} from "modules/hooks";
import {IMAGES_URL, LOCALE} from "modules/constants";
import {QuestionContainer, QuestionWrapper} from "components/Questions/common";
import {ReactComponent as CornerIcon} from "assets/images/icons/corner.svg";
import {FinalPageDescription} from "./Description";
import {get} from "lodash";
import {Squad} from "modules/stores/Models";
import {getTime, createConnextraScriptTag, ConnextraType} from "modules/utils";
import {SupportedTeamCta} from "./SupportedTeamCta";
import {FantasyCta} from "./FantasyCta";

const Wrapper = styled(QuestionWrapper)`
	background: #010101;
	min-height: 90vh;

	@media (max-width: 840px) {
		min-height: 100vh;
	}
`;

const Container = styled(QuestionContainer)`
	padding-top: 8vh;

	@media (max-width: 840px) {
		padding-top: 8vh;
	}
`;

const MediaWrapper = styled.div`
	display: flex;
	width: 100%;
	padding: 30px 0 22px;

	@media (max-width: 840px) {
		flex-direction: column;
	}
`;

const MediaItem = styled.div`
	position: relative;
	display: flex;
	background: rgba(0, 0, 0, 0.5);

	&:after {
		content: "";
		position: absolute;
		bottom: 0;
		left: 0;
		background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 48.44%);
		height: 47%;
		width: 100%;
	}
`;

const MediaVideo = styled(MediaItem)`
	min-width: 50%;
	margin-right: 22px;

	&.hidden:after {
		content: none;
	}

	@media (max-width: 840px) {
		width: 100%;
		margin: 0 0 16px;
	}
`;

const WrapperMediaHistory = styled.div`
	position: relative;
	width: 50%;

	@media (max-width: 840px) {
		width: 100%;
	}
`;

const MediaHistoryContainer = styled.div`
	display: flex;
	flex-direction: column;
	position: absolute;
	left: 0;
	top: 0;
	width: 100%;
	height: 100%;

	@media (max-width: 840px) {
		position: static;
	}
`;

const MediaHistory = styled(MediaItem)`
	width: 100%;
	height: calc(50% - 7px);
	background: #000000;
	margin-bottom: 14px;

	@media (max-width: 840px) {
		height: auto;
		margin: 0 0 16px 0;
	}
`;

const Image = styled.img`
	width: 100%;
	object-fit: cover;
`;

const VideoInner = styled.div`
	width: 100%;
	position: relative;

	.react-awesome-player-container {
		.vjs-big-play-button {
			width: 38px;
			height: 38px !important;
			background: ${({theme}) => theme.background.primary};

			.vjs-icon-placeholder {
				font-size: 32px;
				line-height: 32px;
			}
		}
	}
`;

const TeamTitle = styled.div`
	font-size: 24px;
	border-bottom: 1px solid transparent;
	transition: all 0.2s;

	@media (max-width: 1200px) {
		font-size: 20px;
	}

	@media (max-width: 1000px) {
		font-size: 18px;
	}

	@media (max-width: 900px) {
		font-size: 16px;
	}

	@media (max-width: 768px) {
		font-size: 12px;
	}
`;

const NextGame = styled.a`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: calc(50% - 7px);
	position: relative;
	overflow: hidden;
	padding: 4px 10px;

	& > svg {
		z-index: -1;
		position: absolute;
		top: 0;
		left: 0;
		height: 70%;
		width: auto;

		&:last-child {
			left: auto;
			right: 0;
			transform: scaleX(-1);
		}

		@media (max-width: 840px) {
			height: 90%;
		}
	}

	@media (max-width: 840px) {
		height: 90px;
	}

	&:hover ${TeamTitle} {
		border-color: white;
	}
`;

const SquadLogo = styled.div`
	background: #ffffff;
	border-radius: 50%;
	width: 15%;
	padding-top: 15%;
	height: 0;
	position: relative;

	img {
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
		width: 75%;
	}

	@media (max-width: 840px) {
		width: 12%;
		padding-top: 12%;
	}

	@media (max-width: 500px) {
		width: 14%;
		padding-top: 14%;
	}
`;

const Info = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	font-family: Hind, sans-serif;
	text-align: center;
	height: 100%;
`;

const InfoDesc = styled.div`
	margin-bottom: 10px;

	@media (max-width: 1000px) {
		font-size: 14px;
		margin-bottom: 4px;
	}
`;

const TeamName = styled.div`
	font-size: 20px;
	font-weight: 600;

	@media (max-width: 1000px) {
		font-size: 18px;
	}

	@media (max-width: 500px) {
		font-size: 16px;
	}
`;

const TeamDate = styled.div`
	margin-top: 8px;

	@media (max-width: 1000px) {
		font-size: 14px;
		margin-top: 4px;
	}
`;

const MediaHistoryInner = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	bottom: 19px;
	left: 30px;
	position: absolute;
	width: calc(100% - 60px);
	z-index: 1;

	@media (max-width: 840px) {
		flex-direction: column;
		align-items: flex-start;
		left: 12px;
		bottom: 14px;
		width: calc(100% - 24px);
	}
`;

const VideoContent = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	transition: 0.5s opacity 0.5s;
	opacity: 1;
	position: absolute;
	left: 28px;
	bottom: 33px;
	width: calc(100% - 45px);
	z-index: 1;

	&.hidden {
		opacity: 0;
		pointer-events: none;
	}

	@media (max-width: 1200px) {
		left: 22px;
		bottom: 20px;
		width: calc(100% - 44px);
	}

	@media (max-width: 840px) {
		left: 16px;
		bottom: 7px;
		width: calc(100% - 32px);
	}
`;

const Title = styled.div`
	font-size: 32px;
	line-height: 42px;
	font-weight: 700;
	pointer-events: none;

	@media (max-width: 1200px) {
		font-size: 20px;
		line-height: 26px;
	}

	@media (max-width: 840px) {
		font-size: 20px;
	}
`;

const VideoDesc = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	font-size: 14px;
	font-weight: 600;
	line-height: 22px;
	margin-right: 5px;

	p {
		width: 100%;
		max-width: 60%;
	}

	@media (max-width: 840px) {
		.desktop {
			display: none;
		}

		p {
			max-width: none;
		}
	}
`;

const AdTitle = styled.p`
	font-size: 20px;
	line-height: 26px;

	@media (max-width: 840px) {
		margin-bottom: 12px;
	}
`;

const ThirdCTA = styled.a`
	width: 100%;
	display: block;

	img {
		width: 100%;
	}
`;

const getSquadIcon = (squads: Squad[], id?: number) => {
	const squad = squads.find((squad) => squad.id === id);

	return `${IMAGES_URL}image/logos/${squad?.shortName || ""}.svg`;
};

export const FinalPage: React.FC = observer(() => {
	const {UISquadList, I18n} = useStore();
	const {isMobile} = useIsMobile("(max-width: 840px)");
	const [isVideoPlaying, setVideoPlaying] = useState(false);
	const topSquad = UISquadList.topSquad;
	const name = topSquad?.shortName;
	const title = get(topSquad?.adMainTitle, LOCALE, "");
	const adMainImages = isMobile ? topSquad?.adMainImgMobile : topSquad?.adMainImg;
	const adMainImg = get(adMainImages, LOCALE, "");
	const adMainVideo = get(topSquad?.adMainVideo, LOCALE, "");
	const adFirstBtnLink = get(topSquad?.adFirstBtnLink, LOCALE, "");
	const adFirstTitle = get(topSquad?.adFirstTitle, LOCALE, "");
	// const adFirstBtnTxt = get(topSquad?.adFirstBtnTxt, LOCALE, "");
	const adFirstImages = isMobile ? topSquad?.adFirstImgMobile : topSquad?.adFirstImg;
	const adFirstImg = get(adFirstImages, LOCALE, "");
	const awayCornerColor = get(topSquad?.match, "awayTeamColor", "");
	const awaySquadIcon = getSquadIcon(
		UISquadList.items as Squad[],
		get(topSquad?.match, "awayTeamId", 0)
	);
	const awayTeamAbbr = get(topSquad?.match, "awayTeamAbbr", "");
	const homeCornerColor = get(topSquad?.match, "homeTeamColor", "");
	const homeSquadIcon = getSquadIcon(
		UISquadList.items as Squad[],
		get(topSquad?.match, "homeTeamId", 0)
	);
	const homeTeamAbbr = get(topSquad?.match, "homeTeamAbbr", "");
	const time = get(topSquad?.match, "start_at", "");
	const formattedDate = getTime(time, I18n.t.bind(I18n), LOCALE);
	const adSecondBtnLink = get(topSquad?.adSecondBtnLink, LOCALE, "");
	const adSecondTitle = get(topSquad?.adSecondTitle, LOCALE, "");
	// const adSecondBtnTxt = get(topSquad?.adSecondBtnTxt, LOCALE, "");

	const adThirdImages = isMobile ? topSquad?.adThirdImgMobile : topSquad?.adThirdImg;
	const adThirdImg = get(adThirdImages, LOCALE, "");
	const adThirdBtnLink = get(topSquad?.adThirdBtnLink, LOCALE, "");

	const startPlaying = useCallback(() => {
		createConnextraScriptTag(ConnextraType.VIDEO);
		setVideoPlaying(true);
	}, [setVideoPlaying]);

	const videoOptions = useMemo(
		() => ({
			poster: `${IMAGES_URL}image/${adMainImg}`,
			sources: [
				{
					type: "video/mp4",
					src: `${IMAGES_URL}image/${adMainVideo}`,
				},
			],
		}),
		[adMainVideo, adMainImg]
	);

	const trackClick = (type: ConnextraType) => {
		return () => createConnextraScriptTag(type);
	};

	return (
		<Wrapper>
			<Container>
				<FinalPageDescription />

				<ThirdCTA
					href={adThirdBtnLink}
					target="_blank"
					rel="nofollow noreferrer"
					onClick={trackClick(ConnextraType.VISION)}>
					<img src={`${IMAGES_URL}image/${adThirdImg}`} alt="" />
				</ThirdCTA>

				<MediaWrapper>
					<MediaVideo className={isVideoPlaying ? "hidden" : ""}>
						<VideoInner>
							<ReactAwesomePlayer play={startPlaying} options={videoOptions} />
							<VideoContent className={isVideoPlaying ? "hidden" : ""}>
								<VideoDesc>
									<Title>{title}</Title>
								</VideoDesc>
							</VideoContent>
						</VideoInner>
					</MediaVideo>

					<WrapperMediaHistory>
						<MediaHistoryContainer>
							<MediaHistory
								as="a"
								target="_blank"
								rel="nofollow noreferrer"
								onClick={trackClick(ConnextraType.SHOP)}
								href={adFirstBtnLink}>
								<Image src={`${IMAGES_URL}image/${adFirstImg}`} />
								<MediaHistoryInner>
									<VideoDesc>
										<AdTitle>{adFirstTitle}</AdTitle>
									</VideoDesc>
								</MediaHistoryInner>
							</MediaHistory>

							{topSquad?.match && (
								<NextGame
									href={adSecondBtnLink}
									target="_blank"
									onClick={trackClick(ConnextraType.TICKETS)}
									rel="nofollow noreferrer">
									<CornerIcon color={awayCornerColor} />

									<SquadLogo>
										<img src={awaySquadIcon} alt="" />
									</SquadLogo>

									<Info>
										<TeamTitle>{adSecondTitle}</TeamTitle>

										<div>
											<InfoDesc>
												{I18n.t("screens.final.next_game", {name})}
											</InfoDesc>
											<TeamName>
												{awayTeamAbbr} @ {homeTeamAbbr}
											</TeamName>
											<TeamDate>{formattedDate}</TeamDate>
										</div>

										<div />
									</Info>

									<SquadLogo>
										<img src={homeSquadIcon} alt="" />
									</SquadLogo>

									<CornerIcon color={homeCornerColor} />
								</NextGame>
							)}
						</MediaHistoryContainer>
					</WrapperMediaHistory>
				</MediaWrapper>

				<FantasyCta />

				<SupportedTeamCta />
			</Container>
		</Wrapper>
	);
});
